export type ResourceKey =
  | 'default'
  | 'groups'
  | 'users'
  | 'roles'
  | 'permissions'
  | 'resourceServers'
  | 'umrsRoles'
  | 'issuers'
  | 'loginPages'
  | 'emailTemplates'
  | 'profile'
  | 'playground'
  | 'signin';

export interface ResolvedResource {
  key: ResourceKey;
  entityId?: string;
}

export function resolveResource(pathname: string): ResolvedResource {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] === 'groups') {
    return { key: 'groups', entityId: segments[1] };
  }
  if (segments[0] === 'users') {
    return { key: 'users', entityId: segments[1] };
  }
  if (segments[0] === 'roles') {
    return { key: 'roles' };
  }
  if (segments[0] === 'permissions') {
    return { key: 'permissions' };
  }
  if (segments[0] === 'resource-servers') {
    return { key: 'resourceServers' };
  }
  if (segments[0] === 'umrs-roles') {
    return { key: 'umrsRoles' };
  }
  if (segments[0] === 'identity-providers') {
    return { key: 'issuers' };
  }
  if (segments[0] === 'login-pages') {
    return { key: 'loginPages' };
  }
  if (segments[0] === 'email-templates') {
    return { key: 'emailTemplates' };
  }
  if (segments[0] === 'profile') {
    return { key: 'profile' };
  }
  if (segments[0] === 'playground') {
    return { key: 'playground' };
  }

  return { key: 'default' };
}

// Maps each resource to a markdown file under docs/sdk/. The content is authored
// in markdown so it can be edited (and kept in sync with the integration docs)
// without touching component code.
const RESOURCE_SLUG: Record<ResourceKey, string> = {
  default: 'overview',
  groups: 'groups',
  users: 'users',
  roles: 'roles',
  permissions: 'permissions',
  resourceServers: 'resource-servers',
  umrsRoles: 'umrs-roles',
  issuers: 'identity-providers',
  loginPages: 'login-pages',
  emailTemplates: 'email-templates',
  profile: 'profile',
  playground: 'playground',
  signin: 'signin',
};

// Eagerly load every markdown doc as a raw string. Keys are absolute-ish module
// paths ending in `/<slug>.md`. Adding a new doc requires a dev-server restart
// (or a re-save of this file) so Vite re-evaluates the glob.
const markdownModules = import.meta.glob('../../../../docs/sdk/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function findMarkdown(slug: string): string | undefined {
  const match = Object.entries(markdownModules).find(([modulePath]) =>
    modulePath.endsWith(`/${slug}.md`),
  );
  return match?.[1];
}

export function getSdkDocMarkdown(key: ResourceKey): string {
  return findMarkdown(RESOURCE_SLUG[key]) ?? findMarkdown(RESOURCE_SLUG.default) ?? '';
}
